import { useAtomValue } from 'jotai';
import { BriefcaseIcon, BuildingOfficeIcon, UserIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'next-i18next';

import { PresentationCard } from '@restworld/ui-components';
import { Heading } from '@restworld/ui-ds-base';
import { WorkerResearchInfo } from '@restworld/utility-types';

import {
  jobTypeAtom,
  restaurantTypesAtom,
  workerPreferencesAtom,
  workerRolesAtom,
  workerShiftsAtom,
} from '../../../../employer-job-listing.atoms';
import { TabCardWrapper } from '../candidate-profile-section';

export const WorkerPreferences = () => {
  const { t } = useTranslation();
  const workerPreference = useAtomValue(workerPreferencesAtom);
  const errorMessage = (
    <Heading size={5} color="muted">
      {t('missing_info')}
    </Heading>
  );
  const sector = (workerPreference?.research_info as unknown as WorkerResearchInfo)?.domain_interest_var?.label;
  const location = useAtomValue(restaurantTypesAtom);
  const role = useAtomValue(workerRolesAtom);
  const shifts = useAtomValue(workerShiftsAtom);
  const jobTypes = useAtomValue(jobTypeAtom);

  return (
    <div className="pb-4">
      <TabCardWrapper title={t('preferences')}>
        <div id="preferences" className="flex flex-col items-start gap-4 pt-4">
          <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-8 lg:gap-y-4 gap-4 w-full">
            <PresentationCard
              label={t('common:sector')}
              header={sector || errorMessage}
              icon={<BriefcaseIcon className="w-6 h-6" />}
              contentStyling={'text-gray-500'}
            />
            <PresentationCard
              label={t('common:local')}
              header={location || errorMessage}
              icon={<BuildingOfficeIcon className="w-6 h-6" />}
            />
            <PresentationCard
              label={t('common:role')}
              header={role || errorMessage}
              icon={<UserIcon className="w-6 h-6" />}
            />
          </div>
          <div className="flex flex-col gap-4 items-start w-full mt-6">
            <Heading size={5} weight="medium">
              {t('common:working_hours')}
            </Heading>
            <div className="grid lg:grid-cols-2 grid-cols-1 gap-4 w-full">
              {shifts.map((shift, index) => (
                <div key={index} className="flex flex-row gap-2 items-center justify-start">
                  <CheckCircleIcon className="w-6 h-6 min-w-[24px] text-primary-700" />
                  <Heading size={5} weight="normal" color="muted-foreground">
                    {shift}
                  </Heading>
                </div>
              ))}
              {shifts.length === 0 && errorMessage}
            </div>
          </div>
          <div className="flex flex-col gap-4 items-start w-full mt-6">
            <Heading size={5} weight="medium">
              {t('common:work_commitment')}
            </Heading>
            <div className="grid lg:grid-cols-2 grid-cols-1 gap-4 w-full">
              {jobTypes
                ? jobTypes.map((job, index) => (
                    <div key={index} className="flex flex-row gap-2 items-center justify-start">
                      <CheckCircleIcon className="w-6 h-6 min-w-[24px] text-primary-700" />
                      <Heading size={5} weight="normal" color="muted-foreground">
                        {job.job_type_var.label}
                      </Heading>
                    </div>
                  ))
                : null}
              {!jobTypes?.length && errorMessage}
            </div>
          </div>
        </div>
      </TabCardWrapper>
    </div>
  );
};
